<script setup lang="ts">
import { Navigation, Pagination } from 'swiper';
import type { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { LeftArrowIcon, RightArrowIcon } from '@glow/shared/components/icons';
import PartnerLocation, {
  type PartnerLocationData,
} from './PartnerLocation.vue';

const props = withDefaults(
  defineProps<{
    modelValue?: number;
    locations?: PartnerLocationData[];
  }>(),
  {
    modelValue: undefined,
    locations: () => [],
  }
);
const emit = defineEmits<{
  (e: 'update:modelValue', modelValue?: number): void;
}>();

const swiperRef = ref<SwiperClass>();
const currentIndex = ref<number>();

watch(currentIndex, (value) => {
  emit('update:modelValue', value);
});
watch(
  () => props.modelValue,
  (value) => {
    currentIndex.value = value;
    swiperRef.value?.slideTo(value ?? 0);
  },
  { immediate: true }
);

function onSwiper(swiper: SwiperClass) {
  swiperRef.value = swiper;
}
</script>

<template>
  <div v-if="props.locations.length === 0" class="p-2 text-center">
    Нет рабочих мест
  </div>
  <Swiper
    v-else
    :modules="[Navigation, Pagination]"
    :navigation="
      props.locations.length > 1 && {
        prevEl: '.slider__prev',
        nextEl: '.slider__next',
      }
    "
    :initial-slide="currentIndex"
    pagination
    class="slider"
    @swiper="onSwiper"
    @slide-change="({ activeIndex }) => (currentIndex = activeIndex)"
  >
    <SwiperSlide v-for="location in props.locations" :key="location.id">
      <PartnerLocation class="align-items-center" :location="location" />
    </SwiperSlide>
    <template v-if="props.locations.length > 1">
      <AppButton
        class="slider__prev"
        variant="link"
        :icon-end="LeftArrowIcon"
      />
      <AppButton
        class="slider__next"
        variant="link"
        :icon-end="RightArrowIcon"
      />
    </template>
  </Swiper>
</template>

<style lang="scss" scoped>
.slider {
  height: auto !important;
  text-align: center;
  position: relative;
  padding-bottom: var(--app-spacing-7);
  overflow: hidden;

  &__prev,
  &__next {
    position: absolute;
    top: 30% !important;
    color: var(--app-color-black-tertiary);
    background: none;
    border: none;
    z-index: 5;

    &:hover {
      color: var(--app-color-primary);
    }

    svg {
      width: 28px;
      height: 28px;
    }
  }

  &__prev:disabled,
  &__next:disabled {
    display: none;
  }

  &__prev {
    left: 5px;
  }

  &__next {
    right: 5px;
  }
}
</style>
